/**
 * 翻译管理器
 * 处理翻译API调用和结果处理的业务逻辑
 * (Refactored Version)
 *
 * 核心功能：
 * 1. 封装翻译流程，提供统一的接口
 * 2. 处理翻译上下文准备和优化
 * 3. 统一处理翻译错误和异常情况
 * 4. 集成翻译模型映射和提供商选择
 * 5. (Future) 集成精确选择缓存策略
 * 6. 管理翻译状态和生命周期
 */
import { ModelMapperService, TranslatorFactory, validateTranslationRequest, } from '@readsavor/utils';
import { LoggerService } from '../../logging/services/logger.service.js';
import { TranslationStorageService } from '../../storage/services/TranslationStorageService.js';
/**
 * 翻译管理器实现类
 * 使用单例模式，统一管理翻译相关的业务逻辑
 */
export class TranslationManager {
    /**
     * 私有构造函数，实现单例模式
     */
    constructor() {
        this.translatorFactory = TranslatorFactory;
        this.storageService = TranslationStorageService.getInstance();
        this.modelMapper = ModelMapperService;
        this.logger = LoggerService.getInstance();
    }
    /**
     * 获取单例实例
     * @returns TranslationManager的唯一实例
     */
    static getInstance() {
        if (!TranslationManager.instance) {
            TranslationManager.instance = new TranslationManager();
        }
        return TranslationManager.instance;
    }
    // --- Private Helper Methods for Response Creation ---
    createCoreApiSuccessResponse(data) {
        return { success: true, data };
    }
    createCoreApiErrorResponse(message, code = 'CORE_ERROR', details) {
        const apiError = { message, code, details };
        return { success: false, error: apiError };
    }
    // --- Public API Methods ---
    /**
     * Streams a translation with context. (Refactored)
     * This method now expects the context to be pre-calculated and passed in as simple strings.
     */
    async *translateWithContextStream(text, beforeText, afterText, sourceLanguage, targetLanguage, model, options) {
        // DEBUG LOG - Initial arguments
        const { documentIdentity, selection, bypassCache = false } = options || {};
        const hasValidSelection = documentIdentity && selection;
        if (!bypassCache && hasValidSelection) {
            try {
                const cachedResult = await this.storageService.getTranslation(documentIdentity, selection);
                if (cachedResult) {
                    if (cachedResult.originalText === text) {
                        if (typeof cachedResult.translatedText === 'object' &&
                            cachedResult.translatedText !== null &&
                            'translatedText' in cachedResult.translatedText) {
                            yield cachedResult.translatedText;
                        }
                        else {
                            yield {
                                originalText: cachedResult.originalText,
                                translatedText: cachedResult.translatedText,
                                provider: cachedResult.provider,
                            };
                        }
                        return;
                    }
                }
            }
            catch (cacheError) {
                this.logger.error('Core Stream: Error checking cache', { error: cacheError });
            }
        }
        const provider = this.modelMapper.getModelProvider(model, true);
        const translationRequest = {
            text: text,
            sourceLanguage,
            targetLanguage,
            provider,
            model: provider,
            options: {
                beforeText: beforeText,
                afterText: afterText,
                documentIdentity,
                selection,
                bypassCache,
            },
        };
        // DEBUG LOG - Final TranslationRequest object for stream
        const validation = validateTranslationRequest(translationRequest);
        if (!validation.isValid) {
            const msg = validation.error || 'Invalid translation request';
            const LOG_PREFIX_TM_STREAM = '[TranslationManager Stream]';
            this.logger.error(`${LOG_PREFIX_TM_STREAM} Invalid translation request: ${msg}`, {
                details: translationRequest,
            });
            throw new Error(msg);
        }
        try {
            const translator = await this.translatorFactory.getTranslator(provider);
            if (!translator.translateStream) {
                const msg = `Provider ${provider} does not support streaming.`;
                const LOG_PREFIX_TM_STREAM = '[TranslationManager Stream]';
                this.logger.error(`${LOG_PREFIX_TM_STREAM} ${msg}`);
                throw new Error(msg);
            }
            yield* translator.translateStream(translationRequest);
        }
        catch (error) {
            console.error('>>> [C3] TranslationManager caught error:', error);
            throw error;
        }
    }
    /**
     * 获取可用的翻译提供商列表 (Refactored)
     */
    async getTranslationProviders() {
        try {
            const providers = await this.translatorFactory.getAvailableProviders();
            return this.createCoreApiSuccessResponse(providers);
        }
        catch (error) {
            const msg = error instanceof Error ? error.message : 'Failed to get translation providers';
            this.logger.error('Core: Error getting translation providers', { error });
            return this.createCoreApiErrorResponse(msg, 'GET_PROVIDERS_ERROR');
        }
    }
    /**
     * 获取翻译统计信息 (Refactored)
     */
    async getTranslationStats(documentIdentity) {
        try {
            let totalCount = 0;
            const providerCounts = {};
            if (documentIdentity) {
                const translations = await this.storageService.getTranslationsForTextDocument(documentIdentity);
                totalCount = translations.length;
                translations.forEach(t => {
                    providerCounts[t.provider] = (providerCounts[t.provider] || 0) + 1;
                });
            }
            else {
                totalCount = await this.storageService.getTranslationCount();
            }
            return this.createCoreApiSuccessResponse({
                count: totalCount,
                providers: documentIdentity ? providerCounts : undefined,
            });
        }
        catch (error) {
            this.logger.error('Core: Failed to get translation stats', { error, documentIdentity });
            return this.createCoreApiErrorResponse('Failed to retrieve translation statistics.', 'STATS_ERROR');
        }
    }
    /**
     * 清除翻译缓存 (Refactored)
     */
    async clearTranslationCache(documentIdentity) {
        try {
            await this.storageService.deleteTranslationsForTextDocument(documentIdentity);
            return this.createCoreApiSuccessResponse({ success: true, documentIdentity });
        }
        catch (error) {
            this.logger.error('Core: Failed to clear translation cache', { error, documentIdentity });
            return this.createCoreApiErrorResponse('Failed to clear translation cache.', 'CACHE_CLEAR_ERROR');
        }
    }
    /**
     * 检查服务是否可用 (Refactored)
     */
    async isAvailable() {
        try {
            const providers = await this.translatorFactory.getAvailableProviders();
            const available = providers.some(p => p.isAvailable);
            return available;
        }
        catch (error) {
            this.logger.error('Core: Error checking availability', { error });
            return false;
        }
    }
    /**
     * 获取指定模型的翻译提供商 (Kept from original, uses utils)
     */
    getProviderForModel(model, needsId = false) {
        return this.modelMapper.getModelProvider(model, needsId);
    }
}
TranslationManager.instance = null;
//# sourceMappingURL=TranslationManager.js.map